import { cn } from "@/lib/utils";
import apiRoutes from "@/src/apiRoutes";
import EmptyPage from "@/src/components/EmptyPage/EmptyPage";
import { textVariants } from "@/src/components/Text/Text";
import { Card } from "@/src/components/shad/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/src/components/shad/dropdown";
import api from "@/src/utils";
// import pmImage from "@assets/paymentM.svg";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { MoreHorizontal, Star, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface Props {
  pm: UserPaymentInfo[] | undefined;
}

const AvailablePayments = ({ pm }: Props) => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.paymentMethods.table.${key}`);
  const queryClient = useQueryClient();
  async function action({
    type,
    id,
  }: {
    type: "delete" | "make_default";
    id: number;
  }) {
    const res = await api.post(apiRoutes.addPaymentMethod, {
      type,
      payment_id: id,
    });
    return res.data;
  }

  const { mutate } = useMutation({
    mutationKey: ["payment_action"],
    mutationFn: action,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        queryClient.invalidateQueries({ queryKey: ["user_payment_methods"] });
      } else {
        toast.warning(data?.info);
      }
    },
  });

  if (isEmpty(pm))
    return (
      <EmptyPage
        description="Add a paymemt method to continue"
        message="No payment methods yet"
      />
    );

  return (
    <div>
      <div className="md:grid hidden gap-5">
        {pm?.map((item) => (
          <Card
            key={item?.id}
            className="grid grid-cols-5 p-6 bg-white rounded-3xl"
          >
            <span className={cn(textVariants(), "flex items-center  gap-3")}>
              <img
                src={item?.service__image}
                alt="payment"
                className="h-8 w-8 rounded-full"
              />
              {/* {item?.default && <StarFilledIcon className='text-yellow-500 h-4 w-4  -ml-4' />}  */}
              <div className="grid gap-1">
                <span
                  className={textVariants({
                    case: "capitalize",
                    variant: "desc",
                    size: "sm",
                  })}
                >
                  {trans("headers.accountType")}
                </span>
                <span>{item?.service__name}</span>
              </div>
            </span>
            <span className={cn(textVariants(), "text-left")}>
              <div className="grid gap-1">
                <span
                  className={textVariants({
                    case: "capitalize",
                    variant: "desc",
                    size: "sm",
                  })}
                >
                  {trans("headers.accountName")}
                </span>
                <span>{item?.account_name}</span>
              </div>
            </span>
            <span className={cn(textVariants(), "text-left")}>
              <div className="grid gap-1">
                <span
                  className={textVariants({
                    case: "capitalize",
                    variant: "desc",
                    size: "sm",
                  })}
                >
                  {trans("headers.accountNumber")}
                </span>
                <span>{item?.account_number}</span>
              </div>
            </span>
            <span
              className={cn(textVariants(), "flex justify-end items-center")}
            >
              <div className="bg-[#EAF7F3] dark:bg-green-200 rounded-3xl px-3 text-center py-1 border border-[#2CAF88]">
                <span
                  className={cn(
                    textVariants({
                      variant: "link",
                      case: "capitalize",
                      size: "sm",
                    })
                  )}
                >
                  {item?.payment_type === "both"
                    ? trans("both")
                    : item?.payment_type === "credit"
                    ? trans("sell")
                    : trans("buy")}
                </span>
              </div>
            </span>
            <div className="flex flex-col justify-between gap-5 h-full items-end">
              {item?.default && (
                <span className="rounded-xl dark:bg-grey-600 bg-gray-200 px-2  py-1 font-bold  text-xs text-blue-500 r">
                  {item?.default && `Default`}
                </span>
              )}
              <span className="flex  w-full items-center justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <div>
                      <MoreHorizontal className="h-6 w-6 cursor-pointer dark:text-gray-300 text-gray-500" />
                      <span className="sr-only">Action</span>
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center">
                    <DropdownMenuItem
                      onClick={() =>
                        mutate({ id: item?.id, type: "make_default" })
                      }
                      className="flex items-center gap-2"
                    >
                      <Star className="text-yellow-500 h-4 w-4" />{" "}
                      {trans("actions.default")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => mutate({ id: item?.id, type: "delete" })}
                      className="flex items-center gap-2"
                    >
                      <Trash2 className="text-red-500 h-4 w-4" />{" "}
                      {trans("actions.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            </div>
          </Card>
        ))}
      </div>
      <div className="gap-3 grid md:hidden">
        {pm?.map((item) => (
          <Card className="flex justify-between" key={item.id}>
            <div className="flex gap-2  p-3">
              <img
                src={item?.service__image}
                alt="payment"
                className="h-8 w-8 rounded-full mt-2"
              />
              <div className="grid">
                <span className={textVariants({ font: "semibold" })}>
                  {item?.service__name}{" "}
                  {item?.default && (
                    <span className="rounded-xl dark:bg-grey-600 bg-gray-200 px-2  py-1 font-bold  text-xs text-blue-500 r">
                      {item?.default && `Default`}
                    </span>
                  )}
                </span>
                <span className={textVariants({ size: "sm" })}>
                  {item?.account_name}
                </span>
                <span
                  className={textVariants({
                    variant: "desc",
                    size: "xs",
                    font: "semibold",
                  })}
                >
                  {item?.account_number}
                </span>
              </div>
            </div>
            <span className="flex flex-col items-end justify-between p-2 gap-4 ">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div>
                    <MoreHorizontal className="h-6 w-6 mr-4 dark:text-gray-300 cursor-pointer text-gray-500" />
                    <span className="sr-only">Action</span>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  <DropdownMenuItem
                    onClick={() =>
                      mutate({ id: item?.id, type: "make_default" })
                    }
                    className="flex items-center gap-2"
                  >
                    <Star className="text-yellow-500 h-4 w-4" />{" "}
                    {trans("actions.default")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => mutate({ id: item?.id, type: "delete" })}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="text-red-500 h-4 w-4" />{" "}
                    {trans("actions.delete")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <div>
                <span className={textVariants()}>
                  {(item?.payment_type === "both" ||
                    item?.payment_type === "credit") && (
                    <span className="px-2 py-0.5 border border-gray-200 text-black dark:text-white rounded-xl font-bold text-xs">
                      {item?.payment_type === "both"
                        ? trans("both")
                        : item?.payment_type === "credit"
                        ? trans("sell")
                        : trans("buy")}
                    </span>
                  )}
                </span>
              </div>
            </span>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AvailablePayments;
